<script setup lang="ts">
import Button from '~/components/design-system/button.vue';
import UserSettingsBaseContainer from '~/components/user/user-settings-base-container.vue';
import GenericResponse from '~/components/user-interface/generic-error.vue';
import type { Severity } from '~/interfaces/dto/general/toast-messages';
import type {
  IBankingAccountSettingsRequest as IBankingPreferences,
  ICommunicationPreferencesRequest,
} from '~/interfaces/dto/auth/request';
import SettingsForm from '~/components/account/settings/manage/settings-form.vue';
import Tabs from '~/components/design-system/tabs/tabs.vue';
import Tab from '~/components/design-system/tabs/tab.vue';

type TAccountSettingsOption = 'account' | 'banking';

const { $t, $trackEvent } = useNuxtApp();
const { $authService } = useServices();

const communicationModel = ref<ICommunicationPreferencesRequest>({
  allowEmail: false,
  allowSms: false,
  allowPushNotification: false,
});

const bankingPreferences = ref<IBankingPreferences>({
  allowWithdrawalReversal: true,
});

const initialBankingPreferences = ref<IBankingPreferences | null>(null);

const initialCommunicationModel = ref<ICommunicationPreferencesRequest | null>(
  null,
);

const hasDifference = (
  current: ICommunicationPreferencesRequest | IBankingPreferences,
  initial: ICommunicationPreferencesRequest | IBankingPreferences,
) => {
  for (const key of Object.keys(initial)) {
    if (current[key] !== initial[key]) return true;
  }
  return false;
};

const accountDirty = computed(() => {
  if (!initialCommunicationModel.value) return false;
  return hasDifference(
    communicationModel.value,
    initialCommunicationModel.value,
  );
});

const bankingDirty = computed<boolean>(() => {
  if (!initialBankingPreferences.value) return false;
  return hasDifference(
    bankingPreferences.value,
    initialBankingPreferences.value,
  );
});

const accountDisabled = computed<boolean>(() => {
  return activeLoader.value || !accountDirty.value;
});

const bankingDisabled = computed<boolean>(() => {
  return activeLoader.value || !bankingDirty.value;
});

const hideBanking = ref<boolean>(false);
const hideCommunications = ref<boolean>(false);
const activeLoader = ref<boolean>(true);
const responseType = ref<Severity>();
const responseCode = ref<string>('');
const displayError = ref<boolean>(false);
const errorRef = ref<string>('');
const accountTab = ref<TAccountSettingsOption>('banking');

const handleError = (event: string) => (errorRef.value = event);

const updateAccountSettings = async () => {
  if (!accountDirty.value) return;
  responseCode.value = '';
  activeLoader.value = true;

  try {
    await $trackEvent('Account', 'MY_ACCOUNT_SETTINGS_UPDATE_ACTION');

    const data = await $authService.updateCommunicationsPreferences(
      communicationModel.value,
    );

    if (data || data === '') {
      responseType.value = 'success';
      responseCode.value = 'account-settings-updated';
      initialCommunicationModel.value = { ...communicationModel.value };
    } else {
      responseType.value = 'danger';
      responseCode.value = 'account-settings-error';
    }
  } finally {
    activeLoader.value = false;
  }
};
const updateBankingSettings = async () => {
  if (!bankingDirty.value) return;
  responseCode.value = '';
  activeLoader.value = true;

  try {
    await $trackEvent('Account', 'MY_ACCOUNT_SETTINGS_UPDATE_ACTION');
    const data = await $authService.upsertBankingAccountSettings(
      bankingPreferences.value,
    );
    if (data?.isSuccessful) {
      responseType.value = 'success';
      responseCode.value = 'banking-settings-updated';
      initialBankingPreferences.value = { ...bankingPreferences.value };
    } else {
      responseType.value = 'danger';
      responseCode.value = 'banking-settings-error';
    }
  } catch (error) {
    console.error(error);
    responseType.value = 'danger';
    responseCode.value = 'banking-settings-error';
  } finally {
    activeLoader.value = false;
  }
};

onBeforeMount(async () => {
  displayError.value = false;
  try {
    const data = await $authService.fetchCommunicationsPreferences();
    communicationModel.value = {
      allowEmail: data?.allowEmail ?? false,
      allowSms: data?.allowSms ?? false,
      allowPushNotification: data?.allowPushNotification ?? false,
    };
    initialCommunicationModel.value = { ...communicationModel.value };

    const bankingData = await $authService.getBankingAccountSettings();
    bankingPreferences.value.allowWithdrawalReversal =
      bankingData?.data?.allowWithdrawalReversal;
    initialBankingPreferences.value = { ...bankingPreferences.value };
  } finally {
    activeLoader.value = false;
  }
});
onMounted(() => {
  $trackEvent('Account', 'MY_ACCOUNT_SETTINGS', {
    to: 'account settings',
  });
});

watch([hideBanking, hideCommunications], () => {
  if (hideBanking.value) accountTab.value = 'account';
  else if (hideCommunications.value) accountTab.value = 'banking';
});
</script>

<template>
  <div class="account-settings sm:mt-9">
    <UserSettingsBaseContainer v-if="!displayError" mini-container class="!p-0">
      <LazyUserInterfaceGenericLoader v-if="activeLoader" :standalone="true" />
      <div v-else class="w-full flex justify-center">
        <Tabs
          v-if="!(hideBanking && hideCommunications)"
          :default-tab="accountTab"
          alternate-bg-colors
          stretch
          header-inner-class="my-1 sm:my-2"
          class="w-full"
        >
          <template #divider>
            <hr class="w-full bg-layer-border" />
          </template>
          <template #tabList>
            <Tab
              v-if="!hideCommunications"
              id="account"
              :title="$t('account-settings')"
            >
              <div class="w-full p-4 pt-3">
                <p
                  v-if="hideBanking"
                  class="font-bold text-base-priority text-lg mb-2"
                >
                  {{ $t('account-settings') }}
                </p>
                <SettingsForm
                  v-if="!hideCommunications"
                  v-model="communicationModel"
                  template-name="manage communication preferences"
                  show-title
                  show-sub-title
                  sub-title-classes="mt-2 mb-1 text-xs"
                  element-container-classes="!flex-row justify-between"
                  input-container-classes="w-full text-xs !max-w-[32%]"
                  class="mb-4"
                  @error="handleError"
                  @empty-schema="hideCommunications = true"
                />

                <Button
                  class="uppercase w-full justify-center text-sm mt-4"
                  rounding="md"
                  :disabled="accountDisabled"
                  @click="updateAccountSettings"
                >
                  {{ $t('update') }}
                </Button>
              </div>
            </Tab>
            <Tab
              v-if="!hideBanking"
              id="banking"
              :title="$t('jpc-banking-settings')"
            >
              <div class="p-4 pt-3">
                <div class="bg-layer rounded-xl">
                  <p class="font-bold my-0">
                    {{ $t('jpc-banking-settings') }}
                  </p>

                  <!-- Banking settings-->
                  <div class="">
                    <SettingsForm
                      v-model="bankingPreferences"
                      template-name="manage banking"
                      show-sub-title
                      sub-title-classes="mt-2 mb-1 text-xs"
                      element-container-classes="!flex-row"
                      input-container-classes="text-xs"
                      @error="handleError($event)"
                      @empty-schema="hideBanking = true"
                    />
                  </div>
                  <div class="text-sm w-full mt-4">
                    <Button
                      class="uppercase w-full justify-center text-sm"
                      rounding="md"
                      :disabled="bankingDisabled"
                      @click="updateBankingSettings"
                    >
                      {{ $t('update') }}
                    </Button>
                  </div>
                </div>
              </div>
            </Tab>
          </template>
        </Tabs>
      </div>
      <div v-if="responseCode" class="h-min pb-3">
        <GenericResponse :state="responseType" :reference-code="errorRef">
          {{ $t(`jpc-${responseCode}`) }}
        </GenericResponse>
      </div>
    </UserSettingsBaseContainer>
    <GenericResponse v-else class="h-min my-3" state="danger">
      {{ $t('there-was-a-problem') }}
    </GenericResponse>
  </div>
</template>
